/**
 * The code in this files handles the creation of the main map.
 * This map is reused in all situations.
 * The map state is directly saved in the URL, as an exception to the general rule.
 */
import Map from 'ol/Map.js';
import View from 'ol/View.js';
import {proj as epsg2056} from '@geoblocks/proj/src/EPSG_2056.js';
import {reprojectSearchParams} from './urlMapNormalizer.ts';
import {disableKvp$, urlSearchParamsChanged$} from '../stores/store-main.ts';
import type {MapBrowserEvent} from 'ol';
import {Overlay} from 'ol';
import {
  bgLayer$,
  edittrackMode$,
  mapClick$,
  map$,
  showCoordinates$,
  visibleAdditionalRouteLayers$,
  visibleLayers$,
  geolocationHelper$,
  showPlaceFromSearch$,
} from '../stores/store-map.ts';
import ElementShortlist from '../elements/maps/map-element-shortlist.ts';
import {
  getMapKvp,
  getURLSearchParams,
  setURLSearchParam,
  setURLSearchParams,
  isEmbed,
} from '../urlHandling/urlSearchParams.ts';
import {SWISS_CENTER, SWISS_EXTENT} from '../helpers/constants.ts';
import type {BaseLayerCode} from '../layers/layerConstants.ts';
import {OVERLAY_LAYER_CODES} from '../layers/layerConstants.ts';
import ElementCoordinates from '../elements/maps/map-element-coordinates.ts';
import {isBgCode} from '../layers/layerHelpers.ts';
import {sendAnalytics} from '../analytics.ts';
import {LayersManager} from './LayersManager.ts';
import {createEdittrackStyleFunction} from './layers/edittracksStyle.ts';
import VectorSource from 'ol/source/Vector.js';
import VectorLayer from 'ol/layer/Vector.js';
import {Z_INDEXES} from './constants.ts';
import {CrosscountryNetworkLayer} from './layers/crosscountry.ts';
import type Layer from 'ol/layer/Layer.js';
import ElementInfoPopup from '../elements/maps/map-element-info-popup.ts';
import {pairwise, startWith} from 'rxjs';
import {GeolocationHelper} from './geolocationHelper.ts';

let bgLayerCode: BaseLayerCode = 'pk';
let shortlistPopup: Overlay | undefined;
let coordinatesPopup: Overlay | undefined;
let infoPopup: Overlay | undefined;
let skipClick = false;
const noClick = isEmbed();
let map: Map;

export const layersManager = new LayersManager();
edittrackMode$.subscribe((mode) => {
  if (mode) {
    skipClick = mode === 'edit';
  }
});

function createMap(): Map {
  const params = getURLSearchParams();
  reprojectSearchParams(params);
  if (!disableKvp$.getValue()) setURLSearchParams(params);

  const {E, N, resolution, bgLayer, layers, centerOnUser} =
    Object.fromEntries(params);
  if (isBgCode(bgLayer)) bgLayerCode = bgLayer;
  bgLayer$.next(bgLayerCode);

  layersManager.activateBaseLayer(bgLayerCode);
  if (!disableKvp$.getValue()) setURLSearchParam('bgLayer', bgLayerCode);

  if (layers !== undefined) {
    if (layers.includes('services'))
      sendAnalytics('event', 'layers', {event_label: 'Service'});
  }

  const view = new View({
    projection: epsg2056,
    resolutions: [650, 500, 250, 100, 50, 20, 10, 5, 2.5, 2, 1.5, 1],
    enableRotation: false,
    extent: epsg2056.getExtent(),
    constrainOnlyCenter: true,
  });

  /** Edittrack layers */
  const trackSource = new VectorSource();
  const trackLayer = new VectorLayer({
    zIndex: Z_INDEXES.TOURS,
    properties: {name: 'trackLayer'},
    source: trackSource,
    style: createEdittrackStyleFunction(trackSource),
  });
  /** End edittrack layers */

  const map = new Map({
    layers: [
      ...Object.values(layersManager.baseLayers),
      ...OVERLAY_LAYER_CODES.map((code) => layersManager[code]),
      ...Object.values(layersManager.landLayers),
      ...Object.values(layersManager.detourLayers),
      ...Object.values(layersManager.logoLayers),
      ...Object.values(layersManager.photoLayers),
      layersManager.shooting,
      layersManager.highlightLayer,
      trackLayer,
    ],
    controls: [],
    maxTilesLoading: 128,
    view: view,
  });

  const geolocationHelper = new GeolocationHelper(map);
  if (centerOnUser !== undefined && centerOnUser !== 'no') {
    geolocationHelper.tracking = true;
    const params = getURLSearchParams();
    params.delete('centerOnUser');
    setURLSearchParams(params);
  }
  geolocationHelper$.next(geolocationHelper);

  if (E !== undefined && N !== undefined && resolution !== undefined) {
    view.setCenter([+E, +N]);
    view.setResolution(+resolution);
  } else if (resolution !== undefined) {
    view.setResolution(+resolution);
    view.setCenter(SWISS_CENTER);
  } else {
    // fit the whole switzerland in the view
    map.once('change:target', () =>
      view.fit(SWISS_EXTENT, {padding: [10, 10, 10, 10]}),
    );
  }

  map.on('moveend', () => {
    if (!disableKvp$.getValue()) {
      setURLSearchParams(getMapKvp(view));
    } else {
      urlSearchParamsChanged$.next(getMapKvp(view));
    }
  });

  let timer: NodeJS.Timeout | undefined;
  const touchDuration = 500;

  const shortlistElement = new ElementShortlist();
  shortlistElement.map = map;
  shortlistPopup = new Overlay({
    element: shortlistElement,
  });
  map.addOverlay(shortlistPopup);

  const popupElement = new ElementCoordinates();
  coordinatesPopup = new Overlay({
    element: popupElement,
  });
  map.addOverlay(coordinatesPopup);

  const infoElement = new ElementInfoPopup();
  infoPopup = new Overlay({
    element: infoElement,
    id: 'info-popup',
  });
  map.addOverlay(infoPopup);

  map.on('singleclick', (evt: MapBrowserEvent<MouseEvent>) => {
    if (noClick) return;
    mapClick$.next(evt.coordinate);
    if (skipClick) {
      skipClick = false;
      return;
    }
    shortlistPopup.setPosition(evt.coordinate);
    showCoordinates$.next(undefined);
    showPlaceFromSearch$.next(undefined);
  });

  map.on('pointermove', (evt: MapBrowserEvent<MouseEvent>) => {
    if (!evt.dragging) {
      const pixel = map.getEventPixel(evt.originalEvent);
      const hit = map.hasFeatureAtPixel(pixel, {
        layerFilter: (layer: Layer) =>
          !(layer instanceof CrosscountryNetworkLayer),
      });
      const wmtsHit = layersManager.WMTSClickableLayers.find((layer) => {
        const data = layer.getData(evt.pixel);
        return data && (<any>data)[3] > 0;
      });
      map.getTargetElement().style.cursor = hit || wmtsHit ? 'pointer' : '';
    } else {
      if (timer) clearTimeout(timer);
    }
  });

  const onCoordinatesShow = (e: MouseEvent | TouchEvent) => {
    const pixel = map.getEventPixel(e);
    const coordinates = map.getCoordinateFromPixel(pixel);
    coordinatesPopup.setPosition(coordinates);
    showCoordinates$.next(coordinates);
  };

  map.getViewport().addEventListener('contextmenu', (e) => {
    e.preventDefault();
    if (edittrackMode$.value !== 'edit') {
      onCoordinatesShow(e);
    }
  });

  map.getViewport().addEventListener('touchstart', (e) => {
    if (e.touches.length === 1) {
      timer = setTimeout(() => {
        skipClick = true;
        if (edittrackMode$.value !== 'edit') {
          onCoordinatesShow(e);
        }
      }, touchDuration);
    }
  });

  map.getViewport().addEventListener('touchend', (e) => {
    if (timer) clearTimeout(timer);
  });

  visibleLayers$
    .pipe(startWith(undefined), pairwise())
    .subscribe(([previousLayers, layers]) => {
      layersManager.activateLayers(previousLayers, layers);
      if (!disableKvp$.getValue()) {
        setURLSearchParam('layers', layers.join(','));
      }
    });

  visibleAdditionalRouteLayers$.subscribe((additionalRouteLayers) => {
    Object.entries(additionalRouteLayers).forEach(([key, value]) => {
      setURLSearchParam(key, value ? 'yes' : 'no');
    });
    layersManager.activateLayers(
      visibleLayers$.getValue(),
      visibleLayers$.getValue(),
    );
  });

  bgLayer$.subscribe((bgLayer) => {
    layersManager.activateBaseLayer(bgLayer);
    if (!disableKvp$.getValue()) {
      setURLSearchParam('bgLayer', bgLayer);
    }
  });
  return map;
}

export function getMainMap(): Map {
  if (!map) {
    map = createMap();
    map$.next(map);
  }
  return map;
}

export function panPopupIntoView(popupCode: 'shortlist' | 'coordinates') {
  if (shortlistPopup && popupCode === 'shortlist') shortlistPopup.panIntoView();
  if (coordinatesPopup && popupCode === 'coordinates')
    coordinatesPopup.panIntoView();
}
